import { useCallback, useEffect, useMemo, useState } from "react";
import { dateLocale } from "@/lib/dateTime";

/** A field the "+ Add filter" picker offers (from the API's FilterFields) */
export interface FilterFieldDef {
  key: string;
  label: string;
  group: string;
  type: "text" | "number" | "date" | "enum" | "tags" | "boolean";
  options?: { value: string; label: string }[];
  suggest?: boolean;
  conditions: { op: string; label: string; arity: "none" | "one" | "two" | "many" | "days" }[];
}

/** One added filter: City is Chennai */
export interface FieldFilterValue {
  field: string;
  op: string;
  value?: string | number | (string | number)[] | null;
}

const MAX = 10;

/**
 * The filters a person added with "+ Add filter", remembered per page and browser (like the fixed filters).
 * `param(fields)` is what goes in the list request as `f`: only filters on fields the API still offers.
 */
export function useFieldFilters(key: string) {
  const storageKey = `crm.fieldfilters.${key}`;
  const [values, setValues] = useState<FieldFilterValue[]>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || "null");
      if (Array.isArray(saved)) return saved.filter((f) => f && typeof f.field === "string" && typeof f.op === "string").slice(0, MAX);
    } catch {
      // Storage unavailable or corrupt: start with none
    }
    return [];
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(values));
    } catch {
      // Not critical
    }
  }, [storageKey, values]);

  const setAll = useCallback((next: FieldFilterValue[] | undefined | null) => setValues(Array.isArray(next) ? next.slice(0, MAX) : []), []);
  const reset = useCallback(() => setValues([]), []);

  const param = useCallback(
    (fields: FilterFieldDef[] | null) => {
      if (!fields) return "";
      const known = new Set(fields.map((f) => f.key));
      const list = values.filter((f) => known.has(f.field));
      return list.length ? JSON.stringify(list) : "";
    },
    [values],
  );

  const activeCount = useMemo(() => values.length, [values]);
  return { values, setAll, reset, param, activeCount, max: MAX };
}

const fmtNumber = (v: unknown) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : String(v ?? "");
};
const fmtDate = (v: unknown) => {
  const s = String(v || "");
  const d = new Date(`${s}T00:00:00`);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString(dateLocale(), { day: "numeric", month: "short", year: "numeric" });
};

/** What a filter chip reads: "City is Chennai", "Budget > 5,00,000", "Last contacted in the last 30 days" */
export function fieldFilterLabel(def: FilterFieldDef | undefined, f: FieldFilterValue) {
  if (!def) return f.field;
  const cond = def.conditions.find((c) => c.op === f.op);
  const verb = cond?.label || f.op;
  const v = f.value;
  if (!cond || cond.arity === "none") return `${def.label} ${verb}`;
  if (cond.arity === "days") return `${def.label} ${verb} ${v} day${Number(v) === 1 ? "" : "s"}`;
  if (cond.arity === "two" && Array.isArray(v)) {
    const [a, b] = def.type === "date" ? v.map(fmtDate) : v.map(fmtNumber);
    return `${def.label} ${verb} ${a} and ${b}`;
  }
  if (cond.arity === "many" && Array.isArray(v)) {
    const names = v.map((x) => def.options?.find((o) => o.value === x)?.label || String(x));
    return `${def.label} ${verb} ${names.length > 2 ? `${names.slice(0, 2).join(", ")} +${names.length - 2}` : names.join(", ")}`;
  }
  if (def.type === "number") return `${def.label} ${verb} ${fmtNumber(v)}`;
  if (def.type === "date") return `${def.label} ${verb} ${fmtDate(v)}`;
  return `${def.label} ${verb} ${v}`;
}
